import React from 'react';
import { motion, useMotionValue, useTransform, type Variants } from 'framer-motion';
import { RotateCcwIcon } from 'lucide-react';
import type { Project } from '../../../data/projects';
import type { DeckDirection } from '../../../hooks/useProjectDeck';
import { ProjectVisual } from './ProjectVisual';
interface ProjectCardProps {
  project: Project;
  isFlipped: boolean;
  reduceMotion: boolean;
  onFlip: () => void;
  onSwipe: (direction: DeckDirection) => void;
}
const SWIPE_DISTANCE = 120;
const SWIPE_VELOCITY = 500;
const EASE_OUT = [0.23, 1, 0.32, 1] as const;
const EASE_IN_OUT = [0.65, 0, 0.35, 1] as const;
export const CARD_RADIUS = 14;

// The outgoing card always sits above the incoming one and ignores pointer input,
// so only one card is ever readable or clickable during a transition.
const cardVariants: Variants = {
  enter: {
    opacity: 0,
    scale: 0.97,
    y: 12,
    x: 0,
    rotate: 0,
    zIndex: 3
  },
  center: {
    opacity: 1,
    scale: 1,
    y: 0,
    x: 0,
    rotate: 0,
    zIndex: 3,
    transition: {
      duration: 0.24,
      ease: EASE_OUT,
      delay: 0.06
    }
  },
  exit: (direction: DeckDirection) => ({
    opacity: 0,
    x: direction === 1 ? -560 : 560,
    rotate: direction === 1 ? -6 : 6,
    zIndex: 4,
    pointerEvents: 'none',
    transition: {
      duration: 0.22,
      ease: EASE_OUT
    }
  })
};

// Faces carry no shadow of their own: repainting a large shadow on every frame of
// a 3D rotation is what made the flip stutter. The shadow lives on the static wrapper.
const faceStyle: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: CARD_RADIUS,
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden'
};
export function ProjectCard({
  project,
  isFlipped,
  reduceMotion,
  onFlip,
  onSwipe
}: ProjectCardProps) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-400, 400], [-6, 6]);
  const meta = [project.location, project.year].filter(Boolean).join(' · ');
  return <motion.article className="absolute inset-0 touch-pan-y" style={{
    x,
    rotate,
    cursor: isFlipped ? 'auto' : 'grab',
    borderRadius: CARD_RADIUS,
    boxShadow: '0 18px 40px rgba(23, 23, 22, 0.08)'
  }} variants={cardVariants} initial="enter" animate="center" exit="exit" transition={reduceMotion ? {
    duration: 0
  } : undefined} drag={isFlipped ? false : 'x'} dragConstraints={{
    left: 0,
    right: 0
  }} dragElastic={0.9} whileDrag={{
    cursor: 'grabbing'
  }} onDragEnd={(_, info) => {
    if (info.offset.x < -SWIPE_DISTANCE || info.velocity.x < -SWIPE_VELOCITY) onSwipe(1);else if (info.offset.x > SWIPE_DISTANCE || info.velocity.x > SWIPE_VELOCITY) onSwipe(-1);
  }} aria-roledescription="project card" aria-label={project.name} data-id="element-160" data-mp="18zq5uc4c9m7g">
      <div className="h-full w-full" style={{
      perspective: 2400
    }} data-id="element-161" data-mp="296lhnaac6jhs">
        <motion.div className="relative h-full w-full" style={{
        transformStyle: 'preserve-3d',
        willChange: 'transform'
      }} initial={false} animate={reduceMotion ? {
        rotateY: isFlipped ? 180 : 0
      } : {
        rotateY: isFlipped ? 180 : 0,
        scale: [1, 0.97, 1]
      }} transition={{
        rotateY: {
          duration: reduceMotion ? 0 : 0.3,
          ease: EASE_IN_OUT
        },
        scale: {
          duration: 0.3,
          ease: EASE_IN_OUT,
          times: [0, 0.5, 1]
        }
      }} data-id="element-162" data-mp="253p623j7rnnc">
          {/* Front */}
          <div className="absolute inset-0 flex flex-col overflow-hidden lg:flex-row" style={{
          ...faceStyle,
          transform: 'rotateY(0deg) translateZ(1px)'
        }} aria-hidden={isFlipped} data-id="element-163" data-mp="13f0certosmnn">
            <div className="h-[36%] w-full flex-shrink-0 sm:h-[42%] md:h-[46%] lg:h-full lg:w-[56%]" data-id="element-164" data-mp="1hcypltw9ud1k">
              <ProjectVisual project={project} data-id="element-165" data-mp="3e58rt55qeqst" />
            </div>
            <div className="flex min-h-0 flex-1 flex-col p-5 sm:p-6 md:p-8 lg:p-14" data-id="element-166" data-mp="3e73bkg59ocvb">
              <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4" style={{
              color: 'var(--muted)'
            }} data-id="element-167" data-mp="3j5wdrcf3shg6">
                <span data-id="element-168" data-mp="2iwmwu4nzzbe8">{project.disciplines.join(', ')}</span>
                <span className="tabular-nums sm:whitespace-nowrap" data-id="element-169" data-mp="2xqruiuxgp1tj">{meta}</span>
              </div>
              <h3 className="mt-3 font-aspekta text-[1.35rem] leading-[1.15] tracking-tight sm:text-2xl md:mt-4 md:text-[1.9rem] md:leading-[1.12] lg:mt-5 lg:text-[2.5rem]" style={{
              color: 'var(--fg)'
            }} data-id="element-170" data-mp="2strogk0m3z2f">
                {project.headline}
              </h3>
              <p className="mt-4 hidden max-w-[52ch] text-base leading-relaxed sm:line-clamp-2 lg:mt-5 lg:line-clamp-none lg:text-lg" style={{
              color: 'var(--muted)'
            }} data-id="element-171" data-mp="2etql8xwvkfjs">
                {project.summary}
              </p>

              <dl className="mt-auto grid grid-cols-2 gap-x-6 pt-5 md:pt-6" data-id="element-172" data-mp="1mathkfz5xvn1">
                {project.metrics.slice(0, 2).map(metric => <div key={metric.label} data-id="element-173" data-mp="3t0em7myr3kzz">
                    <dt className="sr-only" data-id="element-174" data-mp="3szbyfnwq5yoa">{metric.label}</dt>
                    <dd data-id="element-175" data-mp="1mim58un54si">
                      <span className="block font-aspekta text-xl tracking-tight sm:text-2xl lg:text-3xl" style={{
                    color: 'var(--accent)'
                  }} data-id="element-176" data-mp="1n0qytvdcrc1">
                        {metric.value}
                      </span>
                      <span className="font-aspekta block text-sm" style={{
                    color: 'var(--muted)'
                  }} data-id="element-177" data-mp="3o2d6nc0ja3t9">
                        {metric.label}
                      </span>
                    </dd>
                  </div>)}
              </dl>

              <button type="button" onClick={onFlip} onPointerDownCapture={e => e.stopPropagation()} tabIndex={isFlipped ? -1 : 0} className="font-aspekta mt-4 h-11 w-full flex-shrink-0 whitespace-nowrap rounded-full px-6 text-sm transition-opacity duration-150 ease-out hover:opacity-85 sm:w-auto sm:self-start lg:mt-6" style={{
              background: 'var(--fg)',
              color: 'var(--bg)'
            }} data-id="element-178" data-mp="6lv7fp5v63sz">
                Read more
              </button>
            </div>
          </div>

          {/* Back */}
          <div className="absolute inset-0 flex flex-col overflow-hidden" style={{
          ...faceStyle,
          transform: 'rotateY(180deg) translateZ(1px)'
        }} aria-hidden={!isFlipped} data-id="element-179" data-mp="s4468s9pilf9">
            <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto overscroll-contain md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" data-id="element-180" data-mp="j4z8ho5yyw45">
              <div className="border-b p-5 sm:p-6 md:border-b-0 md:border-r md:p-10 lg:p-14" style={{
              borderColor: 'var(--border)'
            }} data-id="element-181" data-mp="e6xpf38jmwso">
                <p className="font-aspekta text-2xl tracking-tight" style={{
                color: 'var(--fg)'
              }} data-id="element-182" data-mp="488759cqjabw">
                  {project.name}
                </p>
                <p className="mt-1 text-sm" style={{
                color: 'var(--muted)'
              }} data-id="element-183" data-mp="1yk6gdbb1o67s">
                  {project.role}
                </p>
                <p className="text-sm" style={{
                color: 'var(--muted)'
              }} data-id="element-184" data-mp="270xawjokwwl4">
                  {meta}
                </p>

                <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-5 pt-5 md:mt-8 md:gap-y-6 md:pt-6" style={{
                borderTop: '1px solid var(--border)'
              }} data-id="element-185" data-mp="2b1h9zsspqa9w">
                  {project.metrics.map(metric => <div key={metric.label} data-id="element-186" data-mp="2kv7smh25rqho">
                      <dt className="sr-only" data-id="element-187" data-mp="1o3mdl4micd8b">{metric.label}</dt>
                      <dd data-id="element-188" data-mp="1o579axkk627r">
                        <span className="block font-aspekta text-xl tracking-tight md:text-2xl" style={{
                      color: 'var(--fg)'
                    }} data-id="element-189" data-mp="1s3wjkhaav3n6">
                          {metric.value}
                        </span>
                        <span className="font-aspekta block text-sm leading-snug" style={{
                      color: 'var(--muted)'
                    }} data-id="element-190" data-mp="2235b73hp066d">
                          {metric.label}
                        </span>
                      </dd>
                    </div>)}
                </dl>
              </div>

              <div className="p-5 sm:p-6 md:p-10 lg:p-14" data-id="element-191" data-mp="2fxi4bbpft4k">
                <p className="max-w-[60ch] text-lg leading-relaxed md:text-xl" style={{
                color: 'var(--fg)'
              }} data-id="element-192" data-mp="2ecjoam9rq1f">
                  {project.summary}
                </p>
                {project.details.map(paragraph => <p key={paragraph} className="mt-4 text-base leading-relaxed md:text-lg" style={{
                color: 'var(--muted)'
              }} data-id="element-193" data-mp="7fs9efze6kq5">
                    {paragraph}
                  </p>)}
              </div>
            </div>
            <div className="flex-shrink-0 px-5 py-4 sm:px-6 md:px-10 lg:px-14" style={{
            borderTop: '1px solid var(--border)'
          }} data-id="element-194" data-mp="h4a6on8gq9p1">
              <button type="button" onClick={onFlip} tabIndex={isFlipped ? 0 : -1} className="font-aspekta inline-flex h-11 w-full items-center justify-center gap-2 rounded-full px-5 text-sm transition-colors duration-150 ease-out sm:h-10 sm:w-auto" style={{
              border: '1px solid var(--border)',
              color: 'var(--fg)',
              background: 'transparent'
            }} data-id="element-195" data-mp="v4bdskfbmdcy">
                <RotateCcwIcon size={14} aria-hidden="true" data-id="element-196" data-mp="1ce5jfug134hr" />
                Back to card
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.article>;
}